"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { format, parseISO, addHours } from "date-fns";
import {
  Package,
  MapPin,
  Calendar,
  Clock,
  Truck,
  Shirt,
  CheckCircle,
} from "lucide-react";

interface OrderItem {
  id: string;
  garmentType: string;
  qty: number;
  notes?: string;
}

interface OrderSummaryProps {
  orderType: "ALTERATION" | "STITCHING";
  items: OrderItem[];
  address: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    pincode: string;
    area?: { name: string };
  } | null;
  pickupSlot: {
    startAt: string;
    endAt: string;
  } | null;
  cutoffEligible: boolean;
  promisedDeliveryAt?: string | null;
}

export function OrderSummary({
  orderType,
  items,
  address,
  pickupSlot,
  cutoffEligible,
  promisedDeliveryAt,
}: OrderSummaryProps) {
  const totalItems = items.reduce((sum, item) => sum + item.qty, 0);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Package className="h-5 w-5 text-primary" />
          <CardTitle className="text-base">Order Summary</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Order Type</span>
          <Badge variant="outline">
            {orderType === "ALTERATION" ? "Alteration" : "Stitching"}
          </Badge>
        </div>

        <Separator />

        <div>
          <div className="flex items-center gap-2 mb-3">
            <Shirt className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">
              Items ({totalItems})
            </span>
          </div>
          <div className="space-y-2">
            {items.map((item, index) => (
              <div
                key={item.id}
                className="flex items-center justify-between text-sm"
              >
                <span className="text-muted-foreground">
                  {item.garmentType || `Item ${index + 1}`}
                </span>
                <span>x{item.qty}</span>
              </div>
            ))}
          </div>
        </div>

        {address && (
          <>
            <Separator />
            <div>
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Pickup Address</span>
              </div>
              <div className="text-sm text-muted-foreground">
                <p>{address.line1}</p>
                {address.line2 && <p>{address.line2}</p>}
                <p>
                  {address.city}, {address.state} - {address.pincode}
                </p>
                {address.area && (
                  <p className="mt-1 text-xs">Area: {address.area.name}</p>
                )}
              </div>
            </div>
          </>
        )}

        {pickupSlot && (
          <>
            <Separator />
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Pickup Schedule</span>
              </div>
              <div className="text-sm text-muted-foreground space-y-1">
                <p>{format(parseISO(pickupSlot.startAt), "EEEE, MMMM d, yyyy")}</p>
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  <span>
                    {format(parseISO(pickupSlot.startAt), "h:mm a")} -{" "}
                    {format(parseISO(pickupSlot.endAt), "h:mm a")}
                  </span>
                </div>
              </div>
            </div>
          </>
        )}

        {promisedDeliveryAt && (
          <>
            <Separator />
            <div className={cutoffEligible ? "bg-emerald-50 -mx-4 px-4 py-3 rounded-lg" : ""}>
              <div className="flex items-center gap-2 mb-2">
                <Truck className={`h-4 w-4 ${cutoffEligible ? "text-emerald-600" : "text-muted-foreground"}`} />
                <span className={`text-sm font-medium ${cutoffEligible ? "text-emerald-700" : ""}`}>
                  Estimated Delivery
                </span>
                {cutoffEligible && (
                  <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                    Fast Track
                  </Badge>
                )}
              </div>
              <p className={`text-sm ${cutoffEligible ? "text-emerald-600" : "text-muted-foreground"}`}>
                {format(parseISO(promisedDeliveryAt), "EEEE, MMMM d, yyyy")}
              </p>
              {cutoffEligible && (
                <p className="text-xs text-emerald-600 mt-1">
                  24-hour delivery eligible
                </p>
              )}
            </div>
          </>
        )}

        <Separator />

        <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg">
          <CheckCircle className="h-4 w-4 text-primary mt-0.5" />
          <div className="text-xs text-muted-foreground">
            <p className="font-medium text-foreground mb-1">What happens next?</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Our rider will pick up your garments</li>
              <li>We'll inspect and send you a detailed quote</li>
              <li>After payment, work begins on your order</li>
              <li>We deliver your garments back to you</li>
            </ol>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
